import levenshtein from 'js-levenshtein';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { type TakeTtsModelInfo } from '#common/models/tts-models';
import { useVoiceSlug } from '#speaker-shop/hooks/useVoiceSlug';
import { loadRecordingsFromStorage, saveRecordingsToStorage } from '#speaker-shop/utils/recordingStorage.ts';
import { trackVoiceGenerations } from '#speaker-shop/utils/tracking.ts';

export interface RecordingModel {
    name?: string;
    text: string;
    takes: TakeModel[];
}

export type TakeModel = {
    takeId: string;
    name?: string;
    speakerVoiceId?: string;
    text: string;
    audio: Blob | string;
    filePath: string;
    isChecked: boolean;
} & (TakeTtsModelInfo);

interface RecordingProps {
    recordings: RecordingModel[];
    addTake: (take: TakeModel) => void;
    deleteTake: (textId: string) => void;
    setCheckState: (textId: string, isChecked: boolean) => void;
    fetchAudioForTake: (textId: string) => Promise<string>;
}

const RecordingContext = createContext<RecordingProps>({} as RecordingProps);

export const useRecordingContext = () => useContext(RecordingContext);

export type UpdateRecordingsFunc = (recordings: RecordingModel[]) => void;

interface Props {
    updateRecordings?: UpdateRecordingsFunc;
    setRecordingsSetter?: (setter: UpdateRecordingsFunc) => void;
    children: ReactNode;
    recordings?: RecordingModel[];
    storageKey?: string;
}

const maxAllowedTextDifferenceInPercent = 5;

export function RecordingProvider({
    updateRecordings,
    children,
    setRecordingsSetter,
    storageKey,
    ...props
}: Props) {
    const { rawT } = useEditableTranslation('audio-generation');
    const [ recordings, setRecordings ] = useState<RecordingModel[]>(props.recordings || []);
    const [ isRestored, setIsRestored ] = useState(false);

    const voiceSlug = useVoiceSlug();
    const speakerSlug = storageKey ?? voiceSlug ?? '';

    useEffect(() => {
        if (typeof window === 'undefined' || isRestored || props.recordings?.length) {
            return;
        }

        const storedRecordings = loadRecordingsFromStorage(speakerSlug);

        if (storedRecordings && storedRecordings.length > 0) {
            // Convert stored recordings to RecordingModel format
            const restoredRecordings: RecordingModel[] = storedRecordings.map(rec => ({
                name: rec.name,
                text: rec.text,
                createdAt: rec.createdAt,
                takes: rec.takes.map(take => ({
                    ...take,
                    audio: ''
                }))
            }));

            setRecordings(restoredRecordings);
            console.log(`✅ Restored ${restoredRecordings.length} recordings from sessionStorage`);
        }

        setIsRestored(true);
    }, [ speakerSlug, isRestored, props.recordings ]);

    useEffect(() => {
        if (typeof window === 'undefined' || !isRestored) {
            return;
        }

        saveRecordingsToStorage(recordings, speakerSlug);
        console.log(`✅ Saved ${recordings.length} recordings to sessionStorage`);
    }, [ recordings, speakerSlug, isRestored ]);

    const fetchAudioForTake = useCallback(async (takeId: string): Promise<string> => {
        try {
            // Find the filePath for this takeId
            let pathToFetch = takeId;

            for (const recording of recordings) {
                const take = recording.takes.find(t => t.takeId === takeId);
                if (take && (take as any).filePath) {
                    pathToFetch = (take as any).filePath;
                    console.log('Found filePath:', pathToFetch);
                    break;
                }
            }

            console.log('Fetching with path:', pathToFetch);

            const response = await fetch(`/api/fetch-audio`, {
                method: 'POST',
                body: JSON.stringify({ filePath: pathToFetch })
            });
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to fetch audio');
            }

            setRecordings(r =>
                r.map(recording => ({
                    ...recording,
                    takes: recording.takes.map(take => take.takeId === takeId ? { ...take, audio: data.url } : take)
                }))
            );

            console.log('✅ Fetched audio for take', takeId);
            return data.url;
        } catch (error) {
            console.error('Error fetching audio for take:', error);
            throw error;
        }
    }, [ recordings, setRecordings ]);

    const addTake = useCallback((take: TakeModel) => {
        setRecordings(r => {
            const result = (() => {
                const recordingToAddTo = r.map(x => ({
                    recording: x,
                    levenshtein: calcLevenshteinPercentage(x.text, take.text)
                })).filter(x => x.levenshtein < maxAllowedTextDifferenceInPercent).toSorted((x, y) =>
                    x.levenshtein - y.levenshtein
                ).map(x => x.recording)[0];

                if (recordingToAddTo) {
                    const name = take.name ?? rawT('take-name', { position: recordingToAddTo.takes.length + 1 });
                    return r.map(x =>
                        x === recordingToAddTo
                            ? {
                                ...x,
                                takes: [ ...x.takes, { ...take, name } ]
                            }
                            : x
                    );
                } else {
                    const newRecording = {
                        name: rawT('recording-name', { position: r.length + 1 }),
                        takes: [ { ...take, name: take.name ?? rawT('take-name', { position: 1 }) } ],
                        text: take.text
                    };
                    return [ ...r, newRecording ];
                }
            })();
            trackVoiceGenerations(result.length, result.reduce((sum, r) => sum + r.takes.length, 0));
            return result;
        });
    }, [ rawT ]);

    const setCheckState = useCallback((takeId: string, isChecked: boolean) => {
        setRecordings(r => {
            const newRecordings = r.map(x => ({
                ...x,
                takes: x.takes.map(y => y.takeId === takeId ? { ...y, isChecked } : y)
            }));
            saveRecordingsToStorage(newRecordings, speakerSlug);
            return newRecordings;
        });
    }, [ setRecordings, speakerSlug ]);

    const deleteTake = useCallback((takeId: string) => {
        setRecordings(r => {
            const result = r.map(x => ({
                ...x,
                takes: x.takes.filter(y => y.takeId !== takeId)
            })).filter(x => x.takes.length > 0);
            trackVoiceGenerations(result.length, result.reduce((sum, r) => sum + r.takes.length, 0));
            return result;
        });
    }, [ setRecordings ]);

    useEffect(() => {
        if (updateRecordings) {
            updateRecordings(recordings);
        }
    }, [ updateRecordings, recordings ]);

    useEffect(() => {
        if (setRecordingsSetter) {
            setRecordingsSetter(setRecordings);
        }
    }, [ setRecordingsSetter, setRecordings ]);

    const value: RecordingProps = {
        recordings,
        setCheckState,
        addTake,
        deleteTake,
        fetchAudioForTake
    };

    return <RecordingContext.Provider value={value}>{children}</RecordingContext.Provider>;
}

function calcLevenshteinPercentage(reference: string, newText: string): number {
    return levenshtein(reference.toLowerCase(), newText.toLowerCase()) / reference.length * 100;
}
